/**
 * Axios interceptor for handling authentication
 * - Adds JWT token to all requests
 * - Intercepts 401/403 responses
 * - Auto-refreshes expired tokens (401)
 * - Detects membership-revocation 403s and triggers org-switch recovery
 * - Redirects to login when necessary
 */

import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { tokenService } from './tokenService';
import { getAuthenticationApi } from '../api/apiInstances';
import { trackEvent } from '../utils/analytics';

const sanitizeEndpoint = (url: string): string => {
    return (url ?? '')
        .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/{id}')
        .replace(/\/\d{5,}/g, '/{id}')
        .split('?')[0];
};

const getErrorCategory = (status: number): string => {
    if (status >= 500) return 'server_error';
    if (status === 422) return 'validation_error';
    if (status === 409) return 'conflict';
    if (status === 404) return 'not_found';
    if (status === 403) return 'forbidden';
    return 'other';
};

let isRefreshing = false;
let membershipRecoveryInProgress = false;
let membershipRecoveryTimestamp = 0;
const RECOVERY_GUARD_MS = 15_000;

let failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

/**
 * Returns true when a 403 response body signals that the session's org membership
 * is no longer valid (revoked / removed / no org selected).
 *
 * Primary check: machine-readable `code: "MEMBERSHIP_INACTIVE"` (added server-side).
 * Fallback: string matching for older server responses.
 *
 * These errors cannot be fixed by retrying with the same token — the fix is to
 * refresh (which heals the org pointer server-side) and switch to a valid org.
 */
const isMembershipError = (data: unknown): boolean => {
    if (data === null || typeof data !== 'object') return false;
    // Primary: machine-readable discriminator
    if ('code' in data && (data as { code: unknown }).code === 'MEMBERSHIP_INACTIVE') {
        return true;
    }
    // Fallback: string matching
    if (!('detail' in data)) return false;
    const detail = (data as { detail: unknown }).detail;
    return (
        typeof detail === 'string' && (
            detail.includes('membership') ||
            detail.includes('No organization selected')
        )
    );
};

/**
 * Setup axios interceptors for authentication and locale
 */
export const setupAxiosInterceptors = () => {
    // Request interceptor: Add Authorization and Accept-Language headers
    axios.interceptors.request.use(
        (config: InternalAxiosRequestConfig) => {
            const token = tokenService.getAccessToken();
            if (token && config.headers) {
                config.headers.Authorization = `Bearer ${token}`;
            }

            // Add Accept-Language header based on user's locale preference
            const locale = localStorage.getItem('dentobook-lang') || 'en';  // Default to 'en'
            if (config.headers) {
                config.headers['Accept-Language'] = locale;
            }

            return config;
        },
        (error: AxiosError) => {
            return Promise.reject(error);
        }
    );

    // Response interceptor: Handle 401/403 errors
    axios.interceptors.response.use(
        (response) => response,
        async (error: AxiosError) => {
            const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

            if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
                // Don't try to refresh on auth/public endpoints — retrying would
                // reuse a consumed Turnstile token or cause an infinite loop.
                if (originalRequest.url?.includes('/auth/login') ||
                    originalRequest.url?.includes('/auth/register') ||
                    originalRequest.url?.includes('/auth/refresh') ||
                    originalRequest.url?.includes('/auth/forgot-password') ||
                    originalRequest.url?.includes('/self-register') ||
                    originalRequest.url?.includes('/public/')) {
                    // Clear tokens and redirect to login if refresh endpoint failed
                    if (originalRequest.url?.includes('/auth/refresh')) {
                        tokenService.clearTokens();
                        redirectToLogin();
                    }
                    return Promise.reject(error);
                }

                // Membership-403: org membership was revoked/removed/suspended.
                // Recovery: refresh token (backend heals org pointer) → list active orgs →
                // switch to first ACTIVE org → store flash message → full page reload.
                // The UI must re-initialize cleanly in the new org context — silently
                // retrying would mix data from different orgs into a stale UI shell.
                if (error.response?.status === 403 && isMembershipError(error.response.data)) {
                    const now = Date.now();
                    // Guard: prevent re-entry (from concurrent requests or Authentication.tsx recovery)
                    if (
                        membershipRecoveryInProgress ||
                        isRefreshing ||
                        (now - membershipRecoveryTimestamp) < RECOVERY_GUARD_MS
                    ) {
                        return Promise.reject(error);
                    }

                    membershipRecoveryInProgress = true;
                    membershipRecoveryTimestamp = now;

                    const storedRefreshToken = tokenService.getRefreshToken();
                    if (!storedRefreshToken) {
                        membershipRecoveryInProgress = false;
                        tokenService.clearTokens();
                        redirectToLogin();
                        return Promise.reject(error);
                    }

                    // Preserve the "remember me" storage location across the recovery.
                    const rememberMe = localStorage.getItem('dentobook_remember_me') === 'true';

                    try {
                        const authApi = getAuthenticationApi();

                        // Step 1: Refresh token — backend _heal_user_org_pointer runs server-side,
                        // switching user.organization_id to the first ACTIVE membership.
                        const refreshResponse = await authApi.refreshToken({ refresh_token: storedRefreshToken });
                        tokenService.setTokens(refreshResponse.data.access_token!, refreshResponse.data.refresh_token!, rememberMe);

                        // Step 2: List all org memberships using the healed token.
                        const orgsResponse = await authApi.getUserOrganizations();
                        const activeOrgs = (orgsResponse.data || []).filter(
                            (org) => org.status === 'active'
                        );

                        if (activeOrgs.length === 0) {
                            // No active memberships at all — must log out.
                            membershipRecoveryInProgress = false;
                            tokenService.clearTokens();
                            redirectToLogin();
                            return Promise.reject(error);
                        }

                        // Step 3: Switch to the first ACTIVE org.
                        // Do NOT retry the original request — the UI must re-initialize cleanly.
                        const target = activeOrgs[0];
                        const selectResponse = await authApi.selectOrganization({ organizationId: target.organizationId });
                        tokenService.setTokens(selectResponse.data.access_token!, selectResponse.data.refresh_token!, rememberMe);

                        // Step 4: Store flash so Authentication.tsx shows a toast after reload.
                        sessionStorage.setItem('dentobook_session_event', JSON.stringify({
                            type: 'org_switched_after_revoke',
                            orgName: target.organizationName,
                        }));

                        // Step 5: Full page reload — re-initializes the UI in the new org context.
                        // membershipRecoveryInProgress intentionally NOT reset here; the page is
                        // about to reload so module state is discarded. Keeping it true prevents
                        // Authentication.tsx's concurrent catch from calling _doLogout.
                        window.location.assign('/');
                    } catch {
                        membershipRecoveryInProgress = false;
                        tokenService.clearTokens();
                        redirectToLogin();
                    }
                    return Promise.reject(error);
                }

                if (isRefreshing) {
                    // If already refreshing, queue this request
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    }).then(token => {
                        if (originalRequest.headers) {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                        }
                        return axios(originalRequest);
                    }).catch(err => {
                        return Promise.reject(err);
                    });
                }

                originalRequest._retry = true;
                isRefreshing = true;

                const refreshToken = tokenService.getRefreshToken();

                if (!refreshToken) {
                    // No refresh token, redirect to login
                    redirectToLogin();
                    return Promise.reject(error);
                }

                try {
                    // Attempt to refresh the token using generated API client
                    const authApi = getAuthenticationApi();
                    const response = await authApi.refreshToken({
                        refresh_token: refreshToken
                    });

                    const { access_token, refresh_token } = response.data;

                    // Store new tokens
                    tokenService.setTokens(access_token, refresh_token);

                    // Update the original request with new token
                    if (originalRequest.headers) {
                        originalRequest.headers.Authorization = `Bearer ${access_token}`;
                    }

                    // Process queued requests
                    processQueue(null, access_token);

                    // Retry the original request
                    return axios(originalRequest);
                } catch (refreshError) {
                    // Refresh failed, clear tokens and redirect to login
                    processQueue(refreshError as AxiosError, null);
                    tokenService.clearTokens();
                    redirectToLogin();
                    return Promise.reject(refreshError);
                } finally {
                    isRefreshing = false;
                }
            }

            const statusCode = error.response?.status;
            if (statusCode && statusCode !== 401) {
                trackEvent('api_error_encountered', {
                    http_status: statusCode,
                    endpoint_pattern: sanitizeEndpoint(error.config?.url ?? ''),
                    error_category: getErrorCategory(statusCode),
                });
            }

            return Promise.reject(error);
        }
    );
};

/**
 * Redirect to login page, saving current location for redirect after login
 */
const redirectToLogin = () => {
    const currentPath = window.location.pathname + window.location.search;

    // Don't save login or register pages as redirect URL
    if (!currentPath.includes('/login') && !currentPath.includes('/register')) {
        tokenService.setRedirectUrl(currentPath);
    }

    window.location.href = '/login';
};
